import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback } from 'react';
import { isChatPane, type PaneKey, paneFromPath, pathForPane, useWorkspace } from './store';

/** Navigation + close behaviour shared by the pane grid, the side drawer and the tab bar. */
export function usePaneActions() {
  const { state, close, closeWhere } = useWorkspace();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routePane = paneFromPath(pathname);
  const go = useCallback((path: string) => void navigate({ to: path }), [navigate]);
  /** The new-chat screen (optionally with an agent preselected). */
  const newChat = useCallback(
    (agent?: string) => void navigate({ to: '/chat', search: { new: true, ...(agent ? { agent } : {}) } }),
    [navigate],
  );
  const closePane = useCallback(
    (k: PaneKey) => {
      const remaining = state.panes.filter((p) => p !== k);
      close(k);
      if (k !== routePane) return;
      // The URL follows the focused pane: fall back to the last conversation, else the last pane.
      const next = [...remaining].reverse().find(isChatPane) ?? remaining[remaining.length - 1];
      go(next ? pathForPane(next) : '/chat');
    },
    [state.panes, close, routePane, go],
  );
  /** Close several panes at once; the URL moves to what is left (else the chats overview). */
  const closePanes = useCallback(
    (drop: (k: PaneKey) => boolean) => {
      const remaining = state.panes.filter((p) => !drop(p));
      closeWhere(drop);
      if (!routePane || !state.panes.includes(routePane) || remaining.includes(routePane)) return;
      const next = [...remaining].reverse().find(isChatPane) ?? remaining[remaining.length - 1];
      go(next ? pathForPane(next) : '/chat');
    },
    [state.panes, closeWhere, routePane, go],
  );
  return { go, newChat, closePane, closePanes, routePane };
}
